import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, Paper, useMediaQuery } from '@mui/material';
import { LineChart, PieChart } from '@mui/x-charts';

interface PinChartModalProps {
  pinId?: string;
  eventsData?: any;
  startDate?: Date;
  endDate?: Date;
  selectedEventTypes?: string[];
}

interface WeeklyData {
  date: Date;
  count: number;
  weekLabel: string;
  isForecast?: boolean;
  isCurrentWeek?: boolean;
}

interface EventTypeData {
  eventType: string;
  data: { date: Date; count: number; weekLabel: string }[];
}

interface EventData {
  date: string;
  eventType: string;
  description: string;
}

// Analysis sayfasındaki renkleri import edelim
const eventTypeColors: Record<string, string> = {
  'Terörizm (TE)': '#D76E3B',
  'Örgütlü Suçlar (OS)': '#F35E5E',
  'Sosyal ve Politik İstikrarsızlık (SP)': '#67C086',
  'Çevresel ve Doğal Afetler (CA)': '#689D7B',
  'Bilgi Güvenliği ve Enformasyon Tehditleri (BG)': '#63ABC3',
  'Bireysel ve Adi Suçlar (BA)': '#4995DB',
  'Göç ve Mülteci Krizleri (GM)': '#8e6c58',
  'Sağlık ve Biyolojik Tehditler (SB)': '#265454',
  'Yasal ve Düzenleyici Uyumsuzluklar (YD)': '#5C7A9A',
  'Ekonomik ve Finansal Tehditler (EF)': '#9B7A6B'
};

const PinChartModal: React.FC<PinChartModalProps> = ({ 
  pinId, 
  eventsData, 
  startDate, 
  endDate,
  selectedEventTypes = [] 
}) => {
  const [monthlyData, setMonthlyData] = useState<WeeklyData[]>([]);
  const [eventTypeData, setEventTypeData] = useState<EventTypeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredEvents, setFilteredEvents] = useState<EventData[]>([]);
  const [last7DaysEvents, setLast7DaysEvents] = useState<{
    date: string;
    eventType: string;
    description: string;
  }[]>([]);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const isTabletScreen = useMediaQuery('(min-width:600px) and (max-width:1199px)');

  useEffect(() => {
    if (!eventsData || !startDate || !endDate) {
      setIsLoading(true);
      return;
    }

    processEventsData(eventsData, startDate, endDate);
    setIsLoading(false);
  }, [eventsData, startDate, endDate, selectedEventTypes]);

  const pieChartData = React.useMemo(() => {
    const eventTypeCountMap = filteredEvents.reduce((acc, event) => {
      acc[event.eventType] = (acc[event.eventType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const entries = Object.entries(eventTypeCountMap).filter(([, count]) => count > 0);
    return entries.map(([eventType, count], index) => ({
      id: eventType,
      value: count,
      label: `${eventType} (${count})`,
      color:
        eventTypeColors[eventType] ||
        `hsl(${index * (360 / Math.max(entries.length, 1))}, 70%, 50%)`,
    }));
  }, [filteredEvents]);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const isTodayRange = (start: Date, end: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return isSameDay(start, today) && isSameDay(end, today);
  };

  const isLastWeekRange = (start: Date, end: Date) => {
    // CustomDateRangePicker'daki "Geçen Hafta" tanımıyla birebir aynı mantık:
    // subWeeks(startOfWeek(new Date()), 1) - subWeeks(endOfWeek(new Date()), 1)
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay(); // 0: Pazar ... 6: Cumartesi

    const startOfCurrentWeek = new Date(today);
    startOfCurrentWeek.setDate(today.getDate() - dayOfWeek);

    const endOfCurrentWeek = new Date(startOfCurrentWeek);
    endOfCurrentWeek.setDate(startOfCurrentWeek.getDate() + 6);

    const expectedStart = new Date(startOfCurrentWeek);
    expectedStart.setDate(startOfCurrentWeek.getDate() - 7);
    expectedStart.setHours(0, 0, 0, 0);

    const expectedEnd = new Date(endOfCurrentWeek);
    expectedEnd.setDate(endOfCurrentWeek.getDate() - 7);
    expectedEnd.setHours(0, 0, 0, 0);

    return isSameDay(start, expectedStart) && isSameDay(end, expectedEnd);
  };

  const processEventsData = (response: any, startDate: Date, endDate: Date) => {
    const rangeStart = new Date(startDate);
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(endDate);
    rangeEnd.setHours(23, 59, 59, 999);

    // Tüm olayları topla
    const allEvents = response.locations
      .filter((location: any) => location.district1 !== "Unknown District")
      .flatMap((location: any) => location.events);

    // Olay türüne göre filtreleme
    let filteredEvents = allEvents;
    if (selectedEventTypes.length > 0) {
      filteredEvents = allEvents.filter((event: any) => 
        selectedEventTypes.includes(event.eventType)
      );
    }

    // Seçili tarih aralığındaki olayları filtrele
    const filteredEventsInRange = filteredEvents.filter((event: any) => {
      const eventDate = new Date(event.date);
      return eventDate >= rangeStart && eventDate <= rangeEnd;
    });

    let newFilteredEvents = filteredEventsInRange
      .filter((event: any) => {
        const eventDate = new Date(event.date);
        return eventDate >= rangeStart && eventDate <= rangeEnd;
      })
      .map((event: any) => ({
        date: new Date(event.date).toLocaleDateString('tr-TR'),
        eventType: event.eventType,
        description: event.description
      }))
      .sort((a: EventData, b: EventData) => {
        const dateA = new Date(a.date.split('.').reverse().join('-'));
        const dateB = new Date(b.date.split('.').reverse().join('-'));
        return dateB.getTime() - dateA.getTime();
      });
    
    setFilteredEvents(newFilteredEvents);
    setLast7DaysEvents(newFilteredEvents);

    const useDailyBuckets = isTodayRange(rangeStart, rangeEnd) || isLastWeekRange(rangeStart, rangeEnd);

    if (useDailyBuckets) {
      const dailyGroups: { [key: string]: number } = {};
      const cursor = new Date(rangeStart);
      while (cursor <= rangeEnd) {
        const dayKey = new Date(cursor.getTime() - (cursor.getTimezoneOffset() * 60000))
          .toISOString()
          .split('T')[0];
        dailyGroups[dayKey] = 0;
        cursor.setDate(cursor.getDate() + 1);
      }

      filteredEventsInRange.forEach((event: any) => {
        const eventDate = new Date(event.date);
        eventDate.setHours(0, 0, 0, 0);
        const dayKey = new Date(eventDate.getTime() - (eventDate.getTimezoneOffset() * 60000))
          .toISOString()
          .split('T')[0];
        if (dailyGroups[dayKey] !== undefined) {
          dailyGroups[dayKey]++;
        }
      });

      const dailyDataArray = Object.entries(dailyGroups)
        .map(([dateKey, count]) => {
          const day = new Date(dateKey + 'T00:00:00');
          day.setMinutes(day.getMinutes() + day.getTimezoneOffset());
          const dayLabel = day.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
          return {
            date: day,
            count,
            weekLabel: dayLabel,
            isForecast: false
          };
        })
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      const eventTypes = Array.from(new Set(filteredEvents.map((event: any) => event.eventType as string)));
      const eventTypeDataArray = eventTypes.map(eventType => {
        const typeEvents = filteredEventsInRange.filter((event: any) => event.eventType === eventType);
        return {
          eventType,
          data: dailyDataArray.map(day => ({
            date: day.date,
            count: typeEvents.filter((event: any) => {
              const eventDate = new Date(event.date);
              const d = new Date(day.date);
              d.setHours(0, 0, 0, 0);
              const nextDay = new Date(d);
              nextDay.setDate(nextDay.getDate() + 1);
              return eventDate >= d && eventDate < nextDay;
            }).length,
            weekLabel: day.weekLabel
          }))
        };
      }) as EventTypeData[];

      setMonthlyData(dailyDataArray);
      setEventTypeData(eventTypeDataArray);
      return;
    }

    // Haftalık grupları oluştur
    const firstMonday = new Date(startDate);
    firstMonday.setHours(0, 0, 0, 0);
    const day = firstMonday.getDay();

    const diff = firstMonday.getDate() - day + (day === 0 ? -6 : 1);
    firstMonday.setDate(diff);

    // Tüm pazartesi tarihlerini oluştur
    let currentMonday = new Date(firstMonday);
    const weeklyGroups: { [key: string]: number } = {};

    while (currentMonday <= endDate) {
      // Tarihi Türkiye saatine göre formatla
      const weekKey = new Date(currentMonday.getTime() - (currentMonday.getTimezoneOffset() * 60000))
        .toISOString()
        .split('T')[0];
      
      weeklyGroups[weekKey] = 0;
      currentMonday.setDate(currentMonday.getDate() + 7);
    }

    // Olayları haftalara dağıt
    filteredEventsInRange.forEach((event: any) => {
      const eventDate = new Date(event.date);
      // Event tarihinin bulunduğu haftanın pazartesi gününü bul
      const eventMonday = new Date(eventDate);
      eventMonday.setHours(0, 0, 0, 0);
      const eventDay = eventMonday.getDay();
      const diffToMonday = eventMonday.getDate() - eventDay + (eventDay === 0 ? -6 : 1);
      eventMonday.setDate(diffToMonday);

      // Tarihi Türkiye saatine göre formatla
      const weekKey = new Date(eventMonday.getTime() - (eventMonday.getTimezoneOffset() * 60000))
        .toISOString()
        .split('T')[0];

      if (weeklyGroups[weekKey] !== undefined) {
        weeklyGroups[weekKey]++;
      }
    });

    // Haftalık verileri diziye dönüştür
    let weeklyDataArray = Object.entries(weeklyGroups)
      .map(([dateKey, count]) => {
        // Pazartesi gününü Türkiye saatine göre ayarla
        const monday = new Date(dateKey + 'T00:00:00');
        monday.setMinutes(monday.getMinutes() + monday.getTimezoneOffset());
        
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const weekLabel = `${monday.getDate()} ${monday.toLocaleDateString('tr-TR', { month: 'short' })} - ${sunday.getDate()} ${sunday.toLocaleDateString('tr-TR', { month: 'short' })}`;

        return {
          date: monday,
          count,
          weekLabel,
          isForecast: false
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // NOTE: We do not overwrite counts with forecasts here.
    // The user needs to see that the current week is "partial" and can look different from full weeks.

    // Mevcut haftayı bul ve filtrele
    // Bugün 13 Ocak ise, 11-17 Ocak arasındaki hafta (11 Ocak Pazartesi başlayan) gösterilmemeli.
    const now = new Date();
    const currentMondayForFilter = new Date(now);
    currentMondayForFilter.setHours(0, 0, 0, 0);
    const currentDayForFilter = currentMondayForFilter.getDay();
    const diffToMondayForFilter = currentMondayForFilter.getDate() - currentDayForFilter + (currentDayForFilter === 0 ? -6 : 1);
    currentMondayForFilter.setDate(diffToMondayForFilter);
    currentMondayForFilter.setHours(0, 0, 0, 0);

    // Tarih karşılaştırması için yardımcı fonksiyon (sadece yıl-ay-gün, timezone'dan bağımsız)
    const getDateOnly = (date: Date): number => {
      // Sadece yıl-ay-gün için timestamp kullan (saat bilgisi olmadan)
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const currentMondayTimestamp = getDateOnly(currentMondayForFilter);

    // Tesis detay grafiğinde mevcut (bitmemiş) haftayı göstermeyelim.
    const weeksInSelectedRangeExcludingCurrent = weeklyDataArray
      .filter((week) => {
        // week.date zaten bir Date objesi, normalize et ve timestamp ile karşılaştır
        const weekMondayTimestamp = getDateOnly(week.date);
        // Mevcut haftayı tamamen hariç tut
        return weekMondayTimestamp !== currentMondayTimestamp;
      })
      .filter((week) => week.date >= rangeStart && week.date <= rangeEnd);

    // Olay tiplerine göre verileri hesapla
    const eventTypes = Array.from(new Set(filteredEvents.map((event: any) => event.eventType as string)));
    
    const eventTypeDataArray = eventTypes.map(eventType => {
      const typeEvents = filteredEventsInRange.filter((event: any) => 
        event.eventType === eventType &&
        new Date(event.date) >= startDate &&
        new Date(event.date) <= endDate
      );

      return {
        eventType,
        data: weeksInSelectedRangeExcludingCurrent.map(week => ({
          date: week.date,
          count: typeEvents.filter((event: any) => {
            const eventDate = new Date(event.date);
            return eventDate >= week.date && eventDate < new Date(week.date.getTime() + 7 * 24 * 60 * 60 * 1000);
          }).length,
          weekLabel: week.weekLabel
        }))
      };
    }) as EventTypeData[];

    setMonthlyData(weeksInSelectedRangeExcludingCurrent);
    setEventTypeData(eventTypeDataArray);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <Typography>Veriler yükleniyor...</Typography>
        </Box>
      ) : isTabletScreen ? (
        // Tablet Layout - Dikey düzen
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Grafik Bölümü - Tablet */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 3
          }}>
            {/* Line Chart */}
            <Box
              ref={chartContainerRef}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: 2,
                border: '1px solid #e0e0e0',
                position: 'relative',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                Haftalık Olay Sayısı
              </Typography>
              {monthlyData.length > 0 && (
                <>
                  <Box id="report-weekly-line-chart-capture">
                    <LineChart
                    xAxis={[{
                      data: monthlyData.map(d => d.weekLabel),
                      scaleType: 'band',
                      tickLabelStyle: {
                        angle: 45,
                        textAnchor: 'start',
                        fontSize: 10
                      }
                    }]}
                    yAxis={[{
                      min: 0
                    }]}
                    series={[
                      {
                        data: monthlyData.map(d => d.count),
                        curve: 'linear',
                        showMark: true,
                        color: '#094151',
                        id: 'actual',
                        valueFormatter: (value) => value ? `Olay Sayısı: ${value}` : null
                      }
                    ]}
                    height={280}
                    margin={{ left: 40, right: 40, top: 16, bottom: 80 }}
                    sx={{
                      '.MuiLineElement-root': {
                        strokeWidth: 2,
                      },
                      '.MuiLineElement-series-forecast': {
                        stroke: '#FF9800'
                      },
                      '.MuiMarkElement-root': {
                        strokeWidth: 2,
                        r: 4,
                        stroke: '#fff',
                        fill: 'currentColor',
                        display: 'inline'
                      },
                      '.MuiMarkElement-series-forecast': {
                        fill: '#FF9800'
                      }
                    }}
                    slotProps={{
                      legend: {
                        hidden: true
                      }
                    }}
                    />
                  </Box>
                </>
              )}
            </Box>

            {/* Pie Chart */}
            <Box sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              p: 2,
              border: '1px solid #e0e0e0',
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                Olay Tiplerine Göre Dağılım
              </Typography>
              {pieChartData.length > 0 && (
                <Box id="report-eventtype-pie-chart-capture">
                  <PieChart
                    series={[
                      {
                        data: pieChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                        arcLabel: (item) => `${item.value}`
                      }
                    ]}
                    height={280}
                    margin={{ right: 120 }}
                    slotProps={{
                      legend: {
                        direction: 'column',
                        position: { vertical: 'middle', horizontal: 'right' },
                        padding: { left: 0, right: 20, top: 0, bottom: 0 },
                        itemMarkWidth: 8,
                        itemMarkHeight: 8,
                        markGap: 5,
                        itemGap: 6,
                        labelStyle: {
                          fontSize: 9,
                        }
                      }
                    }}
                  />
                </Box>
              )}
              {pieChartData.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    Seçili tarih aralığında dağılım gösterilecek olay bulunamadı
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Olay Listesi Bölümü - Tablet */}
          <Paper
            elevation={1}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              p: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
              {startDate instanceof Date && endDate instanceof Date ? 
                `Olay Listesi` :
                'Olaylar'
              }
            </Typography>
            
            {filteredEvents.length > 0 ? (
              <Box sx={{ 
                maxHeight: '300px', 
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mt: 2
              }}>
                <List sx={{ width: '100%', p: 0 }}>
                  {filteredEvents.map((event: EventData, index: number) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start" sx={{ px: 2, py: 1.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                                {event.date}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              <Box 
                                component="span"
                                sx={{ 
                                  backgroundColor: eventTypeColors[event.eventType] || '#999',
                                  color: '#fff',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  display: 'inline-block',
                                  mb: 1,
                                  maxWidth: '100%',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {event.eventType}
                              </Box>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                                sx={{ 
                                  fontSize: '0.8rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  display: 'block',
                                  mt: 1,
                                  lineHeight: 1.4
                                }}
                              >
                                {event.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < filteredEvents.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 4,
                border: '2px dashed #e0e0e0',
                borderRadius: 2,
                mt: 2
              }}>
                <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Seçili tarih aralığında olay kaydı bulunamadı
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      ) : (
        // Desktop Layout - Eski yan yana düzen
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            width: '100%',
          }}
        >
          {/* Sol sütun - Line ve Pie grafikleri */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flex: 2,
            }}
          >
            {/* Line Chart */}
            <Box
              ref={chartContainerRef}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: 4,
                pb: 8,
                border: '1px solid #e0e0e0',
                position: 'relative',
                height: '400px',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Haftalık Olay Sayısı
              </Typography>
              {monthlyData.length > 0 && (
                <>
                  <Box id="report-weekly-line-chart-capture">
                    <LineChart
                    xAxis={[{
                      data: monthlyData.map(d => d.weekLabel),
                      scaleType: 'band',
                      tickLabelStyle: {
                        angle: 45,
                        textAnchor: 'start',
                        fontSize: 12
                      }
                    }]}
                    yAxis={[{
                      min: 0
                    }]}
                    series={[
                      {
                        data: monthlyData.map(d => d.count),
                        curve: 'linear',
                        showMark: true,
                        color: '#094151',
                        id: 'actual',
                        valueFormatter: (value) => value ? `Olay Sayısı: ${value}` : null
                      }
                    ]}
                    height={300}
                    margin={{left: 40, right: 40, top: 16, bottom: 100}}
                    sx={{
                      '.MuiLineElement-root': {
                        strokeWidth: 2,
                      },
                      '.MuiLineElement-series-forecast': {
                        stroke: '#FF9800'
                      },
                      '.MuiMarkElement-root': {
                        strokeWidth: 2,
                        r: 4,
                        stroke: '#fff',
                        fill: 'currentColor',
                        display: 'inline'
                      },
                      '.MuiMarkElement-series-forecast': {
                        fill: '#FF9800'
                      }
                    }}
                    slotProps={{
                      legend: {
                        hidden: true
                      }
                    }}
                    />
                  </Box>
                </>
              )}
            </Box>

            {/* Pie Chart */}
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: 2,
                border: '1px solid #e0e0e0',
                height: '300px',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Olay Tiplerine Göre Dağılım
              </Typography>
              {pieChartData.length > 0 && (
                <Box id="report-eventtype-pie-chart-capture">
                  <PieChart
                    series={[
                      {
                        data: pieChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                        arcLabel: (item) => `${item.value}`
                      }
                    ]}
                    height={250}
                    width={600}
                    margin={{ right: 200 }}
                    slotProps={{
                      legend: {
                        direction: 'column',
                        position: { vertical: 'middle', horizontal: 'right' },
                        padding: { left: 0, right: 20, top: 0, bottom: 0 },
                        itemMarkWidth: 8,
                        itemMarkHeight: 8,
                        markGap: 5,
                        itemGap: 8,
                        labelStyle: {
                          fontSize: 11,
                        }
                      }
                    }}
                  />
                </Box>
              )}
              {pieChartData.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    Seçili tarih aralığında dağılım gösterilecek olay bulunamadı
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Sağ sütun - Son 7 gün olaylar listesi */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              p: 2,
              border: '1px solid #e0e0e0',
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              height: '716px', // 400px (line chart) + 300px (pie chart) + 16px (gap) = 716px
            }}
          >
            <Typography variant="h6" gutterBottom>
              {startDate instanceof Date && endDate instanceof Date ? 
                `Olay Listesi` :
                'Olaylar'
              }
            </Typography>
            
            {filteredEvents.length > 0 ? (
              <List sx={{ width: '100%', p: 0, flex: 1, overflow: 'auto' }}>
                {filteredEvents.map((event: EventData, index: number) => (
                  <React.Fragment key={index}>
                    <ListItem alignItems="flex-start" sx={{ px: 1, py: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 'bold' }}>
                              {event.date}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box component="span">
                            <Box 
                              component="span"
                              sx={{ 
                                backgroundColor: eventTypeColors[event.eventType] || '#999',
                                color: '#fff',
                                px: 1,
                                py: 0.5,
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                display: 'inline-block',
                                mb: 1,
                                maxWidth: '100%',
                                wordBreak: 'break-word'
                              }}
                            >
                              {event.eventType}
                            </Box>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                              sx={{ 
                                fontSize: '0.85rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                display: 'block',
                                mt: 1
                              }}
                            >
                              {event.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredEvents.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <Typography color="text.secondary">Seçili tarih aralığında olay kaydı bulunamadı</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default PinChartModal;
